// Centralized currency formatting utilities (EUR focused).

const EUR_NUMBER_FORMAT = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// Compact notation for large numbers (e.g., €1.2M) - unified locale
const EUR_COMPACT_FORMAT = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1
});

export function formatEuro(amount: number | null | undefined): string {
  if (amount == null || isNaN(amount)) return EUR_NUMBER_FORMAT.format(0);
  return EUR_NUMBER_FORMAT.format(amount);
}

// Higher precision currency (e.g., for FX-influenced unit prices) up to 4 decimals.
const EUR_HIGH_PRECISION_FORMAT = new Intl.NumberFormat('pt-PT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4
});

export function formatEuroHighPrecision(amount: number | null | undefined): string {
  if (amount == null || isNaN(amount)) return EUR_HIGH_PRECISION_FORMAT.format(0);
  return EUR_HIGH_PRECISION_FORMAT.format(amount);
}

export function formatEuroCompact(amount: number | null | undefined): string {
  if (amount == null || isNaN(amount)) return EUR_COMPACT_FORMAT.format(0);
  return EUR_COMPACT_FORMAT.format(amount);
}

export function formatEuroPlain(amount: number | null | undefined, fractionDigits: number = 2): string {
  if (amount == null || isNaN(amount)) amount = 0;
  return amount.toLocaleString('pt-PT', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
}

// Quantity formatter: dynamic precision (up to 6) trimming trailing zeros
export function formatQuantity(value: number | null | undefined, maxFractionDigits: number = 6): string {
  if (value == null || isNaN(value)) return '0';
  // Show up to maxFractionDigits, but trim trailing zeros (no grouping, decimal comma).
  return value.toLocaleString('pt-PT', { maximumFractionDigits: maxFractionDigits, useGrouping: false });
}

// Crypto quantity formatter: support up to 9 fraction digits, trim trailing zeros
export function formatCryptoQuantity(value: number | null | undefined): string {
  return formatQuantity(value, 9);
}

// Percentage helper to keep consistent formatting for percent values adjacent to currency.
export function formatPercent(value: number | null | undefined, fractionDigits: number = 2): string {
  if (value == null || isNaN(value)) return '0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value, fractionDigits)}%`;
}

// Plain number with a fixed number of decimals, Portuguese style (1 234,50).
export function formatNumber(value: number | null | undefined, fractionDigits: number = 2): string {
  if (value == null || isNaN(value)) value = 0;
  return value.toLocaleString('pt-PT', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });
}
